import {useQuery} from "@tanstack/react-query"
import {chatServiceV2} from "services"
import {useRef, useState} from "react"

interface UseReactionUsersProps {
    messageId: number
    reaction: string
    count: number
}

export const useReactionUsers = ({messageId, reaction, count}: UseReactionUsersProps) => {
    const abortControllerRef = useRef<AbortController | null>(null)
    const [isHovered, setIsHovered] = useState(false)

    const query = useQuery(
        ["reaction-users", messageId, reaction, count],
        async () => {
            // Cancel previous request if it exists
            if (abortControllerRef.current) {
                abortControllerRef.current.abort()
            }

            // Create new abort controller
            abortControllerRef.current = new AbortController()

            try {
                const response = await chatServiceV2.getReactionUsers({
                    filter: {
                        messageId,
                        reaction
                    },
                    range: {
                        page: 1,
                        pageSize: count
                    }
                })
                return (response.data || []).map((x) => x.author)
            } catch (error) {
                // Don't throw error if request was aborted
                if (error instanceof Error && error.name === "AbortError") {
                    return []
                }
                throw error
            }
        },
        {
            enabled: isHovered,
            staleTime: 5 * 60 * 1000, // 5 minutes
            cacheTime: 10 * 60 * 1000, // 10 minutes
            retry: false,
            refetchOnWindowFocus: false
        }
    )

    const handleMouseEnter = () => {
        setIsHovered(true)
    }

    const handleMouseLeave = () => {
        setIsHovered(false)
        // Cancel the request when mouse leaves
        if (abortControllerRef.current) {
            abortControllerRef.current.abort()
        }
    }

    return {
        users: query.data || [],
        isLoading: query.isFetching,
        error: query.error,
        handleMouseEnter,
        handleMouseLeave
    }
}
